/**
 * Global setting: display fiat symbol after the amount (true) or before (false).
 * Change this single constant to flip symbol position everywhere.
 */
export const FIAT_SYMBOL_AFTER = true

/**
 * Smart rounding for fiat bet amounts.
 * direction 'up' for minimums (must be >= the real min),
 * direction 'down' for maximums (must be <= the real max).
 */
export function roundFiatBet(amount: number, direction: 'up' | 'down' | 'nearest' = 'nearest'): number {
  if (amount <= 0) return 0

  let step: number
  if (amount >= 50000) step = 5000
  else if (amount >= 10000) step = 1000
  else if (amount >= 5000) step = 500
  else if (amount >= 1000) step = 100
  else if (amount >= 100) step = 10
  else if (amount >= 10) step = 1
  else if (amount >= 1) step = 0.1
  else if (amount >= 0.1) step = 0.01
  else step = 0.001

  if (direction === 'up') return Math.ceil(amount / step) * step
  if (direction === 'down') return Math.floor(amount / step) * step
  return Math.round(amount / step) * step
}

/**
 * Format a fiat amount for display (with currency symbol).
 * Handles large (₩13,500) and small ($0.40) values.
 */
export function formatFiatDisplay(amount: number, symbol: string): string {
  if (amount === 0) return FIAT_SYMBOL_AFTER ? `0${symbol}` : `${symbol}0`
  const abs = Math.abs(amount)

  let formatted: string
  if (abs >= 1000) {
    formatted = Math.round(abs).toLocaleString('en-US')
  } else if (abs >= 10) {
    formatted = Math.round(abs).toString()
  } else if (abs >= 0.1) {
    formatted = abs.toFixed(2)
  } else {
    formatted = abs.toFixed(3)
  }

  return FIAT_SYMBOL_AFTER ? `${formatted} ${symbol}` : `${symbol}${formatted}`
}

/**
 * Format a raw number as a clean decimal string for input display.
 * Removes unnecessary trailing zeros but keeps up to 2 decimal places.
 */
export function formatFiatInputValue(amount: number): string {
  if (isNaN(amount) || amount === 0) return ''
  // up to 6 significant decimal digits, then clean up trailing zeros
  return parseFloat(amount.toFixed(6)).toString()
}
